import { Field } from "@sitecore-jss/sitecore-jss-nextjs";
import React, {
  useState,
  useRef,
  useMemo,
  SyntheticEvent,
  useEffect,
} from "react";
import * as List from "@repo/ui/list";
import { ComponentProps } from "../../../types/component-props";
import { isMobile } from "../../utils";
import Carousel from "../carousel/carousel";
import ProductFeature from "../feature";
import FeatureCarouselHeader from "../feature-carousel-header";
import { Feature } from "../types";
import styles from "./feature-carousel.module.css";

type FeatureCarouselProps = ComponentProps & {
  fields: {
    data: {
      datasource: {
        header: { jsonValue: Field<string> };
        features: { results: Array<Feature> };
      };
    };
  };
};

const FeatureCarousel = ({
  fields: {
    data: {
      datasource: {
        header: { jsonValue: carouselHeaderTitle },
        features: { results: features },
      },
    },
  },
}: FeatureCarouselProps) => {
  const [videoPlayTrack, setVideoPlayTrack] = useState<Record<string, boolean>>(
    {},
  );

  const slideRefs = useRef<(HTMLLIElement | null)[]>([]);
  const mobileView = isMobile();

  const handleSetVideoPlayTrack = (videoId: string) => {
    setVideoPlayTrack((prev) =>
      Object.keys(prev).reduce(
        (acc, key) => ({
          ...acc,
          [key]: key === videoId,
        }),
        {},
      ),
    );
  };

  const handleVideoPlaySwitch = (
    e: SyntheticEvent<HTMLVideoElement, Event>,
  ) => {
    const videoId = e.currentTarget.id;

    setVideoPlayTrack(
      Object.keys(videoPlayTrack).reduce(
        (acc, key) => ({
          ...acc,
          [key]: key === videoId,
        }),
        {},
      ),
    );
  };

  useEffect(() => {
    setVideoPlayTrack(
      features.reduce(
        (acc, feature, index) => ({
          ...acc,
          [`video-${feature.id}`]: index === 0,
        }),
        {},
      ),
    );
  }, [mobileView, features]);

  const slides = useMemo(
    () =>
      features.map((feature, slideIndex) => (
        <List.Item
          data-testid="carousel-slide"
          id={slideIndex.toString()}
          key={feature.id}
          className={styles.slide}
          ref={(el) => {
            slideRefs.current[slideIndex] = el;
          }}
          onClick={() => {
            if (!mobileView) {
              handleSetVideoPlayTrack(`video-${feature.id}`);
            }
          }}
        >
          <ProductFeature
            usedInCarousel
            feature={feature}
            playVideo={videoPlayTrack[`video-${feature.id}`] || false}
            handleVideoPlaySwitch={handleVideoPlaySwitch}
          />
        </List.Item>
      )),
    [videoPlayTrack, mobileView],
  );

  return (
    <div className={styles.container}>
      <FeatureCarouselHeader title={carouselHeaderTitle} />
      <Carousel
        isFeatureCarousel
        slides={slides}
        slideRefs={slideRefs}
        videoPlayTrack={videoPlayTrack}
        handleSetVideoPlayTrack={handleSetVideoPlayTrack}
      />
    </div>
  );
};

export default FeatureCarousel;
